import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  useMediaQuery,
  Autocomplete,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import useImageSelect from '../../hooks/useImageSelect';
import { useLocalStorage } from '../../hooks/useLocalStorage';

const normalizeOfficialValues = (values: any) => {
  const imageIdRaw = values.imageId ?? values.image?.id;
  const imageIdNum =
    imageIdRaw === '' || imageIdRaw == null || imageIdRaw === undefined
      ? null
      : Number(imageIdRaw);
  const pageId =
    values.pageId === '' || values.pageId == null ? null : values.pageId;
  const orderNum = Number(values.order);

  return {
    ...values,
    imageId:
      Number.isFinite(imageIdNum as number) && (imageIdNum as number) > 0
        ? imageIdNum
        : null,
    pageId,
    order: Number.isFinite(orderNum) ? orderNum : 1,
    introdution: values.introdution ?? values.introduction ?? '',
  };
};

const OfficialForm = (props: any) => {
  const {
    entity,
    onSubmit,
    errors,
    handleCancel,
    readonly = false,
    publishers,
    pages,
  } = props;

  const {
    ImageSelectComponent,
    uploadImage,
    uploading,
    pendingImageUpload,
  } = useImageSelect();

  const handleUploaded = (image: any, setFieldValue: any) => {
    setFieldValue('imageId', image.id);
    setFieldValue('image', image);
  };

  const publisherLocalStroage = useLocalStorage('OfficialForm_publisherId');

  const isSmallScreen = useMediaQuery((theme: any) =>
    theme.breakpoints.down('sm')
  );

  const [publisherId, setPublisherId] = useState<any>(
    publisherLocalStroage.value
  );

  useEffect(() => {
    if (!publisherId) {
      setPublisherId(publisherLocalStroage.value);
    }
  }, [publisherLocalStroage.value]);

  return (
    <Formik
      initialValues={{ ...entity, publisherId: publisherId ?? null }}
      enableReinitialize={true}
      validate={(values) => {
        const errors: any = {};
        if (!values.lastName?.trim()) {
          errors.lastName = 'A vezetéknév megadása kötelező';
        }
        if (!values.firstName?.trim()) {
          errors.firstName = 'A keresztnév megadása kötelező';
        }
        // Publikáló csak új képfeltöltéshez kell (nem része a képviselő entitásnak).
        if (pendingImageUpload && !(values.publisherId ?? publisherId)) {
          errors.publisherId =
            'Új kép feltöltéséhez válassz publikálót';
        }
        return errors;
      }}
      onSubmit={(values, { setSubmitting }) => {
        const selectedPublisherId = values.publisherId ?? publisherId;

        let newValues = normalizeOfficialValues(values);

        const submit = () => {
          newValues = normalizeOfficialValues({
            ...values,
            ...newValues,
            imageId: newValues.imageId ?? values.imageId ?? values.image?.id,
          });
          const { publisherId: _omit, ...payload } = newValues;
          onSubmit(payload, setSubmitting);
        };

        if (pendingImageUpload) {
          if (!selectedPublisherId) {
            setSubmitting(false);
            return;
          }
          uploadImage(
            (image: any) => {
              newValues = normalizeOfficialValues({
                ...values,
                image,
                imageId: image?.id,
              });
            },
            'kép',
            selectedPublisherId,
            '-1'
          )?.then(submit);
          return;
        }

        submit();
      }}
    >
      {({
        isSubmitting,
        values,
        touched,
        setFieldValue,
        handleChange,
        errors: validationErrors,
        submitCount,
      }) => {
        const showError = (field: string) =>
          (!!touched[field] || submitCount > 0) && !!validationErrors[field];

        return (
        <Form noValidate>
          <Paper>
            <Grid
              container
              spacing={2}
              sx={{
                justifyContent: "left",
                p: 5
              }}>
              <h2>Vezetés {values.id > 0 ? 'szerkesztés' : 'létrehozás'}</h2>
              <Grid size={12} sx={{
                pt: 5
              }}>
                <Box sx={{
                  color: "error.main"
                }}>
                  {errors.map((error: string) => (
                    <li key={error}>{error}</li>
                  ))}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  value={values.order}
                  onChange={handleChange}
                  label="Sorrend"
                  name="order"
                  type="number"
                  disabled={readonly}
                  fullWidth
                  error={showError('order')}
                  helperText={
                    showError('order')
                      ? (validationErrors.order as string)
                      : undefined
                  }
                />
              </Grid>
              <Grid container spacing={2} size={12}>
                <Grid size={{ xs: 12, md: 'auto' }}>
                  <TextField
                    value={values.title}
                    onChange={handleChange}
                    label="Titulus"
                    name="title"
                    disabled={readonly}
                    fullWidth={isSmallScreen}
                    error={showError('title')}
                    helperText={
                      showError('title')
                        ? (validationErrors.title as string)
                        : undefined
                    }
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      }
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 'auto' }}>
                  <TextField
                    value={values.lastName}
                    onChange={handleChange}
                    label="Vezetéknév"
                    name="lastName"
                    required
                    disabled={readonly}
                    fullWidth={isSmallScreen}
                    error={showError('lastName')}
                    helperText={
                      showError('lastName')
                        ? (validationErrors.lastName as string)
                        : undefined
                    }
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                      htmlInput: {
                        required: false,
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 'auto' }}>
                  <TextField
                    value={values.firstName}
                    onChange={handleChange}
                    label="Keresztnév"
                    name="firstName"
                    required
                    disabled={readonly}
                    fullWidth={isSmallScreen}
                    error={showError('firstName')}
                    helperText={
                      showError('firstName')
                        ? (validationErrors.firstName as string)
                        : undefined
                    }
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                      htmlInput: {
                        required: false,
                      },
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} size={12}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    value={values.role}
                    onChange={handleChange}
                    label="Szerepkör"
                    name="role"
                    disabled={readonly}
                    fullWidth
                    error={!!touched.role && !!validationErrors.role}
                    helperText={
                      !!touched.role &&
                      !!validationErrors.role &&
                      (validationErrors.role as string)
                    }
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      }
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    value={values.membership}
                    onChange={handleChange}
                    label="Tagság"
                    name="membership"
                    disabled={readonly}
                    fullWidth
                    error={
                      !!touched.membership && !!validationErrors.membership
                    }
                    helperText={
                      !!touched.membership &&
                      !!validationErrors.membership &&
                      (validationErrors.membership as string)
                    }
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      }
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    value={values.area}
                    onChange={handleChange}
                    label="Terület"
                    name="area"
                    disabled={readonly}
                    fullWidth
                    error={!!touched.area && !!validationErrors.area}
                    helperText={
                      !!touched.area &&
                      !!validationErrors.area &&
                      (validationErrors.area as string)
                    }
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      }
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} size={12}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    value={values.phone}
                    onChange={handleChange}
                    label="Telefon"
                    name="phone"
                    disabled={readonly}
                    fullWidth
                    error={!!touched.phone && !!validationErrors.phone}
                    helperText={
                      !!touched.phone &&
                      !!validationErrors.phone &&
                      (validationErrors.phone as string)
                    }
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      }
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    value={values.email}
                    onChange={handleChange}
                    label="Email"
                    name="email"
                    disabled={readonly}
                    fullWidth
                    error={!!touched.email && !!validationErrors.email}
                    helperText={
                      !!touched.email &&
                      !!validationErrors.email &&
                      (validationErrors.email as string)
                    }
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      }
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    value={values.fax}
                    onChange={handleChange}
                    label="Fax"
                    name="fax"
                    disabled={readonly}
                    fullWidth
                    error={!!touched.fax && !!validationErrors.fax}
                    helperText={
                      !!touched.fax &&
                      !!validationErrors.fax &&
                      (validationErrors.fax as string)
                    }
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      }
                    }}
                  />
                </Grid>
              </Grid>
              <Grid size={12}>
                <TextField
                  value={values.introdution}
                  onChange={handleChange}
                  label="Bemutatkozás"
                  name="introdution"
                  disabled={readonly}
                  fullWidth
                  multiline
                  error={
                    !!touched.introdution && !!validationErrors.introdution
                  }
                  helperText={
                    !!touched.phone &&
                    !!validationErrors.introdution &&
                    (validationErrors.introdution as string)
                  }
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    }
                  }}
                />
              </Grid>
              <Grid size={12}>
                <Autocomplete
                  disablePortal
                  id="pageId"
                  disabled={readonly}
                  value={values.pageId || null}
                  onChange={(event, value) => {
                    setFieldValue('pageId', value ?? null);
                  }}
                  getOptionLabel={(option) =>
                    pages?.find((g: any) => g.id === option)?.name ?? ''
                  }
                  options={pages?.map((g: any) => g.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={!!touched.pageId && !!validationErrors.pageId}
                      helperText={
                        !!touched.pageId &&
                        !!validationErrors.pageId &&
                        (validationErrors.pageId as string)
                      }
                      label="Oldal"
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <Autocomplete
                  disablePortal
                  id="publisherId"
                  disabled={readonly}
                  value={values.publisherId ?? publisherId ?? null}
                  onChange={(event, value) => {
                    setPublisherId(value);
                    setFieldValue('publisherId', value ?? null);
                    publisherLocalStroage.setLocalStorageValue(value);
                  }}
                  getOptionLabel={(option) =>
                    publishers?.find((g: any) => g.id === option)?.name ?? ''
                  }
                  options={publishers?.map((g: any) => g.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={showError('publisherId')}
                      helperText={
                        showError('publisherId')
                          ? (validationErrors.publisherId as string)
                          : 'Csak új képfeltöltéshez szükséges; a képviselőhöz nem mentjük.'
                      }
                      label="Publikáló (képhez)"
                      inputProps={{
                        ...params.inputProps,
                        required: false,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <ImageSelectComponent
                  image={values.image}
                  onSelect={(image: any) => {
                    setFieldValue('imageId', image.id);
                    setFieldValue('image', image);
                  }}
                  alt="kép"
                  onUploaded={(image: any) =>
                    handleUploaded(image, setFieldValue)
                  }
                  publisherId={values.publisherId ?? publisherId}
                  currentFolderId={'-1'}
                />
              </Grid>
              <Grid
                container
                sx={{
                  pt: 3,
                  justifyContent: "left"
                }}>
                {!readonly && (
                  <Grid size={{ xs: 12, sm: isSmallScreen ? 12 : 'auto' }} sx={{
                    p: 1
                  }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth={isSmallScreen}
                      disabled={isSubmitting || uploading}
                    >
                      Mentés
                    </Button>
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: isSmallScreen ? 12 : 'auto' }} sx={{
                  p: 1
                }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    fullWidth={isSmallScreen}
                    onClick={handleCancel}
                  >
                    Mégse
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Form>
        );
      }}
    </Formik>
  );
};

export default OfficialForm;
